import React, { useState, useEffect, useMemo, useRef } from 'react';
import { calendarApi, vehicleApi } from '../services/api';
import { CaretLeft, CaretRight, Car, MagnifyingGlass, Plus, Wrench } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Link } from 'react-router-dom';
import BlockDialog, { BLOCK_TYPE_MAP } from '../components/BlockDialog';

const MONTHS = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
];

const WEEKDAYS_SHORT = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];

const STATUS_COLORS = {
  confirmed: 'bg-[#FFB800]/90 border-[#FFB800] text-[#1F1500]',
  active:    'bg-[#009951]/90 border-[#009951] text-white',
  completed: 'bg-[#8A94A6]/80 border-[#8A94A6] text-white',
};

const DAY_PX = 38;          // width of one day cell
const ROW_PX = 60;          // height of vehicle row

const toISODate = (d) => d.toISOString().slice(0, 10);
const sameOrAfter = (a, b) => a >= b;
const sameOrBefore = (a, b) => a <= b;

const CalendarPage = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [bookings, setBookings] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  const [blockEditing, setBlockEditing] = useState(null);
  const scrollRef = useRef(null);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth() + 1;

  useEffect(() => { loadAll(); }, [year, month]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [calRes, vehRes] = await Promise.all([
        calendarApi.getCalendar(month, year),
        vehicleApi.getAll(),
      ]);
      setBookings(calRes.data.bookings || []);
      setBlocks(calRes.data.blocks || []);
      setVehicles((vehRes.data || []).sort((a, b) => (a.license_plate || '').localeCompare(b.license_plate || '')));
    } catch (e) {
      console.error('Calendar load error:', e);
    } finally {
      setLoading(false);
    }
  };

  // Build day list for current month
  const days = useMemo(() => {
    const daysInMonth = new Date(year, month, 0).getDate();
    const arr = [];
    for (let i = 1; i <= daysInMonth; i++) {
      const d = new Date(year, month - 1, i);
      arr.push({
        day: i,
        date: d,
        iso: toISODate(d),
        weekday: d.getDay(),
        isWeekend: d.getDay() === 0 || d.getDay() === 6,
        isToday: toISODate(d) === toISODate(new Date()),
      });
    }
    return arr;
  }, [year, month]);

  const monthStart = days[0]?.iso;
  const monthEnd = days[days.length - 1]?.iso;

  // Filter vehicles
  const visibleVehicles = useMemo(() => {
    if (!filter.trim()) return vehicles;
    const q = filter.toLowerCase();
    return vehicles.filter(v =>
      (v.license_plate || '').toLowerCase().includes(q) ||
      (v.brand || '').toLowerCase().includes(q) ||
      (v.model || '').toLowerCase().includes(q)
    );
  }, [vehicles, filter]);

  // Group bookings per vehicle, clip to month
  const bookingsByVehicle = useMemo(() => {
    const map = {};
    for (const b of bookings) {
      if (!b.vehicle_id) continue;
      const bStart = (b.start_date || '').slice(0, 10);
      const bEnd = (b.end_date || '').slice(0, 10);
      if (!bStart || !bEnd) continue;
      // Clip to month boundaries
      const cStart = sameOrAfter(bStart, monthStart) ? bStart : monthStart;
      const cEnd = sameOrBefore(bEnd, monthEnd) ? bEnd : monthEnd;
      if (cEnd < monthStart || cStart > monthEnd) continue;
      // Day offset from month start (1-based)
      const startDay = parseInt(cStart.slice(8, 10), 10);
      const endDay = parseInt(cEnd.slice(8, 10), 10);
      const left = (startDay - 1) * DAY_PX + 2;
      const width = Math.max(DAY_PX - 4, (endDay - startDay + 1) * DAY_PX - 4);
      (map[b.vehicle_id] = map[b.vehicle_id] || []).push({ ...b, _left: left, _width: width, _cStart: cStart, _cEnd: cEnd });
    }
    return map;
  }, [bookings, monthStart, monthEnd]);

  // Group blocks per vehicle, clip to month
  const blocksByVehicle = useMemo(() => {
    const map = {};
    for (const b of blocks) {
      if (!b.vehicle_id) continue;
      const bStart = (b.start_date || '').slice(0, 10);
      const bEnd = (b.end_date || '').slice(0, 10);
      if (!bStart || !bEnd) continue;
      const cStart = sameOrAfter(bStart, monthStart) ? bStart : monthStart;
      const cEnd = sameOrBefore(bEnd, monthEnd) ? bEnd : monthEnd;
      if (cEnd < monthStart || cStart > monthEnd) continue;
      const startDay = parseInt(cStart.slice(8, 10), 10);
      const endDay = parseInt(cEnd.slice(8, 10), 10);
      const left = (startDay - 1) * DAY_PX + 2;
      const width = Math.max(DAY_PX - 4, (endDay - startDay + 1) * DAY_PX - 4);
      (map[b.vehicle_id] = map[b.vehicle_id] || []).push({ ...b, _left: left, _width: width });
    }
    return map;
  }, [blocks, monthStart, monthEnd]);

  const prevMonth = () => setCurrentDate(new Date(year, month - 2, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month, 1));
  const goToday = () => setCurrentDate(new Date());

  // Auto-scroll today into view
  useEffect(() => {
    const todayDay = (() => {
      const t = new Date();
      if (t.getFullYear() === year && t.getMonth() + 1 === month) return t.getDate();
      return null;
    })();
    if (todayDay && scrollRef.current) {
      const px = (todayDay - 1) * DAY_PX - 200;
      scrollRef.current.scrollLeft = Math.max(0, px);
    }
  }, [year, month, loading]);

  const totalWidth = days.length * DAY_PX;

  return (
    <div className="p-6" data-testid="calendar-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-5">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#111418]">Flotten-Kalender</h1>
          <p className="text-sm text-[#525866]">Fahrzeuge links · Buchungen rechts pro Tag</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={prevMonth} className="px-2" data-testid="cal-prev"><CaretLeft weight="bold" size={16} /></Button>
          <Button variant="outline" onClick={goToday} className="px-3" data-testid="cal-today">Heute</Button>
          <div className="text-base font-bold text-[#111418] min-w-[170px] text-center">
            {MONTHS[month - 1]} {year}
          </div>
          <Button variant="outline" onClick={nextMonth} className="px-2" data-testid="cal-next"><CaretRight weight="bold" size={16} /></Button>
        </div>
      </div>

      {/* Legend & filter */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-4 text-xs text-[#525866]">
          <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-[#FFB800]"></span>Bestätigt</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-[#009951]"></span>Aktiv</span>
          <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-[#8A94A6]"></span>Abgeschlossen</span>
        </div>
        <div className="relative w-64">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A94A6]" />
          <Input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Fahrzeug filtern…"
            className="pl-8 h-9 text-sm"
            data-testid="cal-filter"
          />
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-9 gap-1.5"
          onClick={() => { setBlockEditing(null); setBlockDialogOpen(true); }}
          data-testid="cal-add-block-btn"
        >
          <Plus size={14} /> Belegung
        </Button>
      </div>

      {/* Gantt */}
      <div className="card-swiss overflow-hidden" data-testid="cal-gantt">
        {loading ? (
          <div className="p-12 text-center text-[#8A94A6]">Lade Kalender…</div>
        ) : visibleVehicles.length === 0 ? (
          <div className="p-12 text-center text-[#8A94A6]">Keine Fahrzeuge gefunden.</div>
        ) : (
          <div className="flex">
            {/* Left: vehicle column (sticky horizontally) */}
            <div className="shrink-0 border-r border-[#E2E4E9] bg-white sticky left-0 z-20" style={{ width: 230 }}>
              <div className="h-12 px-3 border-b border-[#E2E4E9] flex items-end bg-[#F7F8FA] sticky top-0 z-10">
                <span className="overline text-[#525866]">Fahrzeug ({visibleVehicles.length})</span>
              </div>
              <div>
                {visibleVehicles.map((v) => (
                  <Link
                    key={v.id}
                    to={`/vehicles/${v.id}`}
                    className="flex items-center gap-2 px-3 border-b border-[#E2E4E9] hover:bg-[#F7F8FA] transition-colors"
                    style={{ height: ROW_PX }}
                    data-testid={`cal-vehicle-${v.id}`}
                  >
                    <div className="w-10 h-10 bg-[#F7F8FA] rounded-sm overflow-hidden shrink-0 flex items-center justify-center">
                      {v.image_url ? (
                        <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${v.image_url}`} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <Car size={18} className="text-[#8A94A6]" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-semibold text-[#111418] truncate">{v.brand} {v.model}</p>
                      <p className="text-[11px] text-[#525866] font-mono truncate">{v.license_plate}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            {/* Right: day grid – horizontal scroll only when month wider than screen */}
            <div ref={scrollRef} className="overflow-x-auto">
              {/* Day headers */}
              <div className="h-12 flex border-b border-[#E2E4E9] bg-[#F7F8FA] sticky top-0 z-10" style={{ width: totalWidth }}>
                {days.map((d) => (
                  <div
                    key={d.iso}
                    className={`flex flex-col items-center justify-center border-r border-[#E2E4E9] text-[10px] ${d.isWeekend ? 'bg-[#FAFAFB]' : ''} ${d.isToday ? 'bg-[#FFE5E5]' : ''}`}
                    style={{ width: DAY_PX, minWidth: DAY_PX }}
                  >
                    <span className="text-[#8A94A6]">{WEEKDAYS_SHORT[d.weekday]}</span>
                    <span className={`text-sm font-semibold ${d.isToday ? 'text-[#E60000]' : 'text-[#111418]'}`}>{d.day}</span>
                  </div>
                ))}
              </div>

              {/* Rows */}
              {visibleVehicles.map((v) => (
                <div
                  key={v.id}
                  className="relative border-b border-[#E2E4E9]"
                  style={{ height: ROW_PX, width: totalWidth }}
                >
                  {/* Day grid (vertical lines + today highlight + weekends) */}
                  {days.map((d) => (
                    <div
                      key={d.iso}
                      className={`absolute top-0 bottom-0 border-r border-[#E2E4E9] ${d.isWeekend ? 'bg-[#FAFAFB]' : ''} ${d.isToday ? 'bg-[#FFE5E5]/40' : ''}`}
                      style={{ left: (d.day - 1) * DAY_PX, width: DAY_PX }}
                    />
                  ))}

                  {/* Booking bars */}
                  {(bookingsByVehicle[v.id] || []).map((b) => (
                    <Link
                      key={b.id}
                      to={`/bookings/${b.id}`}
                      className={`absolute top-2 bottom-2 rounded-sm border-l-4 shadow-sm hover:shadow-md transition-shadow flex items-center px-2 text-xs font-medium overflow-hidden ${STATUS_COLORS[b.status] || STATUS_COLORS.completed}`}
                      style={{ left: b._left, width: b._width }}
                      title={`${b.customer?.first_name || ''} ${b.customer?.last_name || ''} · ${b.booking_number || ''} · ${b._cStart} → ${b._cEnd}`}
                      data-testid={`cal-booking-${b.id}`}
                    >
                      <span className="truncate">
                        {b.customer ? `${b.customer.first_name || ''} ${b.customer.last_name || ''}`.trim() : '—'}
                      </span>
                    </Link>
                  ))}
                  {/* Block bars (internal: Werkstatt, Leerfahrt, ...) */}
                  {(blocksByVehicle[v.id] || []).map((b) => {
                    const t = BLOCK_TYPE_MAP[b.block_type] || {};
                    return (
                      <button
                        type="button"
                        key={b.id}
                        onClick={() => { setBlockEditing(b); setBlockDialogOpen(true); }}
                        className="absolute top-2 bottom-2 rounded-sm border-l-4 shadow-sm hover:shadow-md transition-shadow flex items-center px-2 text-xs font-semibold overflow-hidden text-white"
                        style={{
                          left: b._left,
                          width: b._width,
                          background: `repeating-linear-gradient(45deg, ${t.color || '#525866'}, ${t.color || '#525866'} 6px, ${t.color || '#525866'}cc 6px, ${t.color || '#525866'}cc 12px)`,
                          borderLeftColor: t.color || '#525866',
                        }}
                        title={`${b.type_label || b.block_type}${b.notes ? ' · ' + b.notes : ''}`}
                        data-testid={`cal-block-${b.id}`}
                      >
                        <Wrench size={11} className="mr-1 shrink-0" />
                        <span className="truncate">{b.type_label || b.block_type}</span>
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <BlockDialog
        open={blockDialogOpen}
        onOpenChange={setBlockDialogOpen}
        editing={blockEditing}
        onSaved={loadAll}
      />
    </div>
  );
};

export default CalendarPage;
